import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { NoteCard } from './NoteCard';
import { NoteHistoryDto } from '../../services/workNotes';
import { EmptyState } from '../ui';
import './NoteHistoryTimeline.css';

interface NoteHistoryTimelineProps {
  history: NoteHistoryDto[];
  onTogglePin?: (noteId: string) => void;
}

export const NoteHistoryTimeline: React.FC<NoteHistoryTimelineProps> = ({
  history,
  onTogglePin,
}) => {
  const [expandedStages, setExpandedStages] = useState<Set<string>>(
    new Set(history.map(h => h.stage))
  );

  const toggleStage = (stage: string) => {
    setExpandedStages(prev => {
      const next = new Set(prev);
      if (next.has(stage)) {
        next.delete(stage);
      } else {
        next.add(stage);
      }
      return next;
    });
  };

  if (history.length === 0) {
    return (
      <EmptyState
        variant="no-notes"
        title="No note history"
        message="Notes will be organized by stage as you create them throughout your workflow."
      />
    );
  }

  return (
    <div className="note-history-timeline">
      {history.map((stageHistory, index) => (
        <div key={stageHistory.stage} className="note-history-timeline__stage">
          <button
            className="note-history-timeline__stage-header"
            onClick={() => toggleStage(stageHistory.stage)}
          >
            <div className="note-history-timeline__stage-marker">
              <div className="note-history-timeline__stage-dot" />
              {index < history.length - 1 && (
                <div className="note-history-timeline__stage-line" />
              )}
            </div>
            <div className="note-history-timeline__stage-info">
              <span className="note-history-timeline__stage-name">
                {stageHistory.stageDisplayName}
              </span>
              <span className="note-history-timeline__stage-count">
                {stageHistory.noteCount} {stageHistory.noteCount === 1 ? 'note' : 'notes'}
              </span>
            </div>
            <span className="note-history-timeline__stage-chevron">
              {expandedStages.has(stageHistory.stage) ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </span>
          </button>

          {expandedStages.has(stageHistory.stage) && (
            <div className="note-history-timeline__notes">
              {stageHistory.notes.map(note => (
                <NoteCard
                  key={note.id}
                  note={note}
                  onTogglePin={onTogglePin}
                  showStage={false}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default NoteHistoryTimeline;
